import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { 
  Building2, FileSignature, Phone, Mail, Clock, CheckCircle2, 
  MapPin, LogOut, FileText, ArrowRight, User, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

interface ContractData {
  id: string;
  title: string;
  status: string;
  signing_token: string | null;
  created_at: string;
  buyer_lead_id?: string | null;
  seller_lead_id?: string | null;
  created_by?: string;
}

interface AgentData {
  name: string;
  email: string;
  phone: string;
  avatar?: string;
}

interface PropertyData {
  address: string;
  estimatedValue: number;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
}

export default function ClientPortal() {
  const navigate = useNavigate();
  const { currentUser, logout } = useStore();
  const [contracts, setContracts] = useState<ContractData[]>([]);
  const [agent, setAgent] = useState<AgentData | null>(null);
  const [property, setProperty] = useState<PropertyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isBuyer = currentUser?.role === 'buyer';

  useEffect(() => {
    if (currentUser?.email) {
      fetchPortalData();
    }
  }, [currentUser]);

  const fetchPortalData = async () => {
    setIsLoading(true);
    try {
      if (!isSupabaseConfigured || !supabase) {
        // Fallback mock data in demo mode
        setMockData();
        setIsLoading(false);
        return;
      }

      // 1. Fetch leads matching the user's email
      const { data: leads, error: leadError } = await supabase
        .from('leads')
        .select('*')
        .eq('email', currentUser.email);

      if (leadError) throw leadError;

      if (!leads || leads.length === 0) {
        // No matching leads - check if there's an invited contract in profile settings
        const invitedContractId = currentUser.settings?.invited_contract_id;
        const invitedAgentId = currentUser.settings?.invited_by_agent_id;

        if (invitedContractId) {
          const { data: contract, error: contractErr } = await supabase
            .from('contracts')
            .select('*')
            .eq('id', invitedContractId)
            .maybeSingle();

          if (!contractErr && contract) {
            setContracts([contract]);
            if (invitedAgentId) {
              const { data: agentProfile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', invitedAgentId)
                .maybeSingle();
              
              if (agentProfile) {
                setAgent({
                  name: agentProfile.full_name || 'Your Agent',
                  email: agentProfile.email || '',
                  phone: agentProfile.phone || '',
                  avatar: agentProfile.avatar_url || ''
                });
              }
            }
          }
        } else {
          // Put mock data so they see a premium active dashboard on direct sign up
          setMockData();
        }
        setIsLoading(false);
        return;
      }

      const leadIds = leads.map(l => l.id);
      
      // Load property details if buyer
      if (isBuyer && leads[0]) {
        setProperty({
          address: leads[0].address || 'TBD Address',
          estimatedValue: leads[0].property_value || 0,
          bedrooms: leads[0].bedrooms || 0,
          bathrooms: leads[0].bathrooms || 0,
          sqft: leads[0].sqft || 0
        });
      }

      // 2. Fetch contracts linked to these leads
      // Using a string-based query or arrays
      const queryList = leadIds.map(id => `'${id}'`).join(',');
      const { data: contractsData, error: contractsErr } = await supabase
        .from('contracts')
        .select('*')
        .or(`buyer_lead_id.in.(${queryList}),seller_lead_id.in.(${queryList})`);

      if (contractsErr) throw contractsErr;

      setContracts(contractsData || []);

      // 3. Fetch agent info from the first contract or lead creator
      const creatorId = contractsData?.[0]?.created_by || leads[0]?.created_by;
      if (creatorId) {
        const { data: agentProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', creatorId)
          .maybeSingle();
        
        if (agentProfile) {
          setAgent({
            name: agentProfile.full_name || 'Your Agent',
            email: agentProfile.email || '',
            phone: agentProfile.phone || '',
            avatar: agentProfile.avatar_url || ''
          });
        }
      } else {
        // Fallback agent
        setAgent({
          name: "Sarah Jenkins",
          email: "sarah.jenkins@wholescaleagents.com",
          phone: "(512) 555-0199"
        });
      }
    } catch (err: any) {
      console.error('Error fetching client portal data:', err);
      toast.error('Failed to sync portal details.');
      setMockData();
    } finally {
      setIsLoading(false);
    }
  };

  const setMockData = () => {
    // Premium Mock Data
    setContracts([
      {
        id: 'mock-contract-1',
        title: 'Purchase and Sale Agreement — 809 West Ave',
        status: 'pending_signature',
        signing_token: 'demo-token',
        created_at: new Date().toISOString()
      }
    ]);
    setAgent({
      name: "Sarah Jenkins",
      email: "sarah.jenkins@wholescaleagents.com",
      phone: "(512) 555-0199"
    });
    setProperty({
      address: "809 West Ave, Austin, TX 78701",
      estimatedValue: 425000,
      bedrooms: 3,
      bathrooms: 2,
      sqft: 1850
    });
  };

  const handleSignOut = async () => {
    if (supabase) await supabase.auth.signOut();
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#07080d] text-white flex flex-col font-sans select-none animate-fade-in">
      {/* Background Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--t-primary)]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--t-primary)]/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#07080d]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center">
            <Building2 className="text-purple-400 w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-wider uppercase italic">Client Portal</h1>
            <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-black">WholeScale OS Secure Workspace</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex flex-col text-right">
            <span className="text-sm font-bold">{currentUser?.name || 'Client'}</span>
            <span className="text-[10px] text-purple-400 uppercase font-black tracking-widest">
              Verified {isBuyer ? 'Buyer' : 'Seller'}
            </span>
          </div>
          <button 
            onClick={handleSignOut}
            className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
        
        {/* Left column - Dashboard & Next Steps */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Welcome Card */}
          <div className="p-6 bg-zinc-900/40 border border-white/5 rounded-3xl space-y-4">
            <h2 className="text-xl font-bold uppercase italic tracking-wider">
              Welcome, {currentUser?.name?.split(' ')[0] || 'Client'}
            </h2>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Use this portal to review property details, sign contracts securely, and communicate with your real estate agent.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div className="p-4 bg-black/30 rounded-2xl border border-white/5 flex flex-col justify-between">
                <span className="text-[10px] uppercase font-black text-zinc-500 tracking-wider">Account Role</span>
                <span className="text-sm font-bold mt-2 text-white capitalize">{currentUser?.role}</span>
              </div>
              <div className="p-4 bg-black/30 rounded-2xl border border-white/5 flex flex-col justify-between">
                <span className="text-[10px] uppercase font-black text-zinc-500 tracking-wider">Active Contracts</span>
                <span className="text-sm font-bold mt-2 text-purple-400">{contracts.length}</span>
              </div>
              <div className="p-4 bg-black/30 rounded-2xl border border-white/5 flex flex-col justify-between">
                <span className="text-[10px] uppercase font-black text-zinc-500 tracking-wider">Security Gating</span>
                <span className="text-sm font-bold mt-2 text-emerald-400">Verified AAL1</span>
              </div>
            </div>
          </div>

          {/* Active Contracts */}
          <div className="space-y-4">
            <h3 className="text-base font-bold uppercase tracking-wider text-zinc-400 italic">Active Contracts</h3>

            {isLoading ? (
              <div className="text-zinc-500 text-xs">Loading agreement details...</div>
            ) : contracts.length === 0 ? (
              <div className="p-8 bg-zinc-900/20 border border-white/5 rounded-3xl text-center space-y-2">
                <FileText size={36} className="mx-auto text-zinc-600" />
                <p className="text-sm text-zinc-400">No active contracts found</p>
                <p className="text-xs text-zinc-500">Please reach out to your agent to invite you to sign a contract.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contracts.map(contract => (
                  <div key={contract.id} className="p-5 bg-zinc-900/30 border border-white/5 rounded-3xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <FileSignature className="text-purple-400 w-4 h-4" />
                        <h4 className="text-sm font-bold text-white">{contract.title}</h4>
                      </div>
                      <div className="flex items-center gap-4 text-[10px] text-zinc-500 font-bold uppercase tracking-wider">
                        <span>Created: {new Date(contract.created_at).toLocaleDateString()}</span>
                        <span>Status: <span className={contract.status === 'signed' ? 'text-emerald-400' : 'text-amber-400'}>{contract.status.replace('_', ' ')}</span></span>
                      </div>
                    </div>

                    {contract.status !== 'signed' && (
                      <button 
                        onClick={() => {
                          if (contract.signing_token) {
                            navigate(`/sign/${contract.signing_token}`);
                          } else {
                            // Demomode/fallback
                            navigate(`/sign/demo-token`);
                          }
                        }}
                        className="px-4 py-2.5 bg-purple-650 hover:bg-purple-600 text-xs font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 text-white shadow-lg shadow-purple-650/20 hover:scale-[1.02] active:scale-95 shrink-0"
                      >
                        Sign Document <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Property details (for Buyers) */}
          {isBuyer && property && (
            <div className="space-y-4">
              <h3 className="text-base font-bold uppercase tracking-wider text-zinc-400 italic">Property Details</h3>
              <div className="p-6 bg-zinc-900/30 border border-white/5 rounded-3xl grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] uppercase font-black text-zinc-500 tracking-wider block mb-1">Address</span>
                    <span className="text-sm font-medium text-white flex items-start gap-1.5">
                      <MapPin size={16} className="text-zinc-500 shrink-0 mt-0.5" />
                      {property.address}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-black text-zinc-500 tracking-wider block mb-1">Contract Valuation</span>
                    <span className="text-xl font-black text-purple-400">
                      ${property.estimatedValue.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 bg-black/20 p-4 rounded-2xl border border-white/5 h-fit">
                  <div className="text-center">
                    <span className="text-[9px] uppercase font-black text-zinc-500 tracking-wider block mb-1">Beds</span>
                    <span className="text-sm font-bold text-white">{property.bedrooms || 3}</span>
                  </div>
                  <div className="text-center border-x border-white/5">
                    <span className="text-[9px] uppercase font-black text-zinc-500 tracking-wider block mb-1">Baths</span>
                    <span className="text-sm font-bold text-white">{property.bathrooms || 2}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-[9px] uppercase font-black text-zinc-500 tracking-wider block mb-1">Sqft</span>
                    <span className="text-sm font-bold text-white">{property.sqft || 1850}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right column - Agent Info & Next Steps timeline */}
        <div className="space-y-6">
          
          {/* Agent Card */}
          <div className="p-6 bg-zinc-900/40 border border-white/5 rounded-3xl space-y-4">
            <h3 className="text-xs font-black uppercase text-zinc-500 tracking-widest">Your Agent</h3>
            
            {agent ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-purple-650/20 border border-purple-500/20 flex items-center justify-center text-sm font-black text-purple-400 italic uppercase">
                    {agent.avatar ? (
                      <img src={agent.avatar} alt={agent.name} className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      agent.name.split(' ').map(n => n[0]).join('')
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate text-white">{agent.name}</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Licensed Broker</p>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-zinc-400">
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-zinc-500" />
                    <span>{agent.phone || '(512) 555-0100'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-zinc-500" />
                    <span className="truncate">{agent.email}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-zinc-500 text-xs">No agent assigned.</div>
            )}
          </div>

          {/* Next Steps Dashboard */}
          <div className="p-6 bg-zinc-900/40 border border-white/5 rounded-3xl space-y-4">
            <h3 className="text-xs font-black uppercase text-zinc-500 tracking-widest">Next Steps</h3>
            
            <div className="relative border-l border-white/5 pl-4 ml-1 space-y-4">
              <div className="relative">
                <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-purple-500 ring-4 ring-[#07080d]" />
                <p className="text-xs font-bold text-white">Review & E-Sign Contract</p>
                <p className="text-[10px] text-zinc-400 mt-0.5">Open active agreements and apply your secure digital signature.</p>
              </div>

              <div className="relative">
                <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-zinc-700 ring-4 ring-[#07080d]" />
                <p className="text-xs font-bold text-zinc-400">Agent Verification</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">The agent will verify your signatures and seal the transaction.</p>
              </div>

              <div className="relative">
                <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-zinc-700 ring-4 ring-[#07080d]" />
                <p className="text-xs font-bold text-zinc-400">Closing Settlement</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">Closing instructions and settlement details will be posted here.</p>
              </div>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}
